import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Archive,
  Bot,
  CheckCircle2,
  CircleStop,
  Clock3,
  Inbox,
  LoaderCircle,
  LockKeyhole,
  MessagesSquare,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  ShieldAlert,
  Wrench,
  X,
  XCircle,
} from 'lucide-react';
import {
  requestAdminOperationsReport,
  requestCampaignAccessState,
  setCampaignAccessState,
} from '../utils/adminOperationsClient';
import { requestAdminConversationReview } from '../utils/adminConversationReviewClient';
import AdminConversationReviewDrawer from './AdminConversationReviewDrawer';
import ConfirmDialog from './ConfirmDialog';
import PollerHealthPanel from './PollerHealthPanel';
import AdminSendLimits from './AdminSendLimits';
import Header from './Header';
import SideBar from './SideBar';
import '../styles/AdminOperationsPage.css';

const CAMPAIGN_COLUMNS = [
  { key: 'live', label: 'Live' },
  { key: 'stopping', label: 'Stopping' },
  { key: 'stopped', label: 'Stopped' },
  { key: 'stopFailed', label: 'Stop failed' },
  { key: 'completed', label: 'Completed' },
  { key: 'archived', label: 'Archived' },
];

const EMPTY_CONVERSATION_REVIEW = {
  campaigns: [],
  threads: [],
  drafts: [],
  truncation: {
    campaigns: false,
    threads: false,
    messageThreads: [],
    drafts: false,
    contentBodies: { messages: 0, drafts: 0 },
  },
};

function mergeThreadReview(currentReview, threadReview) {
  const loadedThread = threadReview.threads?.[0];
  if (!loadedThread?.id) return currentReview;
  const threads = currentReview.threads.map(thread => (
    thread.id === loadedThread.id ? { ...thread, ...loadedThread } : thread
  ));
  return {
    ...currentReview,
    threads,
    truncation: {
      ...currentReview.truncation,
      messageThreads: threads
        .filter(thread => thread.messagesTruncated)
        .map(thread => thread.id),
      contentBodies: {
        messages: threads.reduce(
          (total, thread) => total + thread.messages.filter(message => message.bodyTruncated).length,
          0
        ),
        drafts: currentReview.drafts.filter(draft => draft.bodyTruncated).length,
      },
    },
  };
}

function formatActivity(value) {
  if (!value) return 'No activity recorded';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'No activity recorded';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function accessCopy(field) {
  return field === 'creationEnabled'
    ? {
        label: 'Campaign creation',
        description: 'Controls whether normal users can begin a new campaign.',
        noun: 'creation',
      }
    : {
        label: 'Campaign automation',
        description: 'Controls whether normal-user campaign work can be processed.',
        noun: 'automation',
      };
}

function accessBannerState(creationOpen, automationOpen) {
  if (creationOpen && automationOpen) {
    return {
      tone: 'is-open',
      Icon: CheckCircle2,
      headline: 'CAMPAIGN OPERATIONS OPEN',
      detail: 'Creation and automation are available to normal users.',
    };
  }
  if (!creationOpen && !automationOpen) {
    return {
      tone: 'is-closed',
      Icon: LockKeyhole,
      headline: 'CAMPAIGN OPERATIONS FULLY CLOSED',
      detail: 'Normal users cannot start campaigns, and nothing sends or processes replies.',
    };
  }
  if (!creationOpen) {
    return {
      tone: 'is-restricted',
      Icon: AlertTriangle,
      headline: 'CAMPAIGN CREATION CLOSED · AUTOMATION RUNNING',
      detail: 'Normal users cannot start new campaigns. Campaigns already live keep sending and processing replies.',
    };
  }
  return {
    tone: 'is-restricted',
    Icon: AlertTriangle,
    headline: 'CAMPAIGN AUTOMATION CLOSED · CREATION OPEN',
    detail: 'Normal users can still start campaigns, but nothing sends and no replies are processed until automation reopens.',
  };
}

function AccessToggle({ field, checked, disabled, onRequest }) {
  const copy = accessCopy(field);
  return (
    <div className={`admin-ops-access__control ${checked ? 'is-open' : 'is-closed'}`}>
      <div>
        <strong>{copy.label}</strong>
        <span>{copy.description}</span>
      </div>
      <button
        type="button"
        className="admin-ops-switch"
        role="switch"
        aria-label={copy.label}
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onRequest(field, !checked)}
      >
        <span aria-hidden="true" />
        {checked ? 'Open' : 'Closed'}
      </button>
    </div>
  );
}

function SummaryMetric({ icon: Icon, label, value, detail, tone = 'neutral' }) {
  return (
    <article className={`admin-ops-metric admin-ops-metric--${tone}`}>
      <Icon size={19} aria-hidden="true" />
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </article>
  );
}

function warningLabel(warning) {
  if (warning.code === 'orphaned_actionable_work') {
    return `${warning.count} ${warning.count === 1 ? 'item' : 'items'} outside live campaigns`;
  }
  if (warning.code === 'blocked_outbox_work') {
    return `${warning.count} blocked ${warning.count === 1 ? 'send' : 'sends'}`;
  }
  return `${warning.count} ${warning.count === 1 ? 'warning' : 'warnings'}`;
}

function UserOperationsRow({ user, onReview, onReviewIssues }) {
  const label = user.displayName || user.email || user.uid || 'Unknown user';
  return (
    <tr>
      <th scope="row">
        <strong>{label}</strong>
        {user.displayName && user.email && <span>{user.email}</span>}
      </th>
      {CAMPAIGN_COLUMNS.map(column => (
        <td key={column.key} data-label={column.label} data-count={column.key}>
          {user.campaigns[column.key]}
        </td>
      ))}
      <td data-label="Active threads">
        <strong className={user.activeThreads ? 'admin-ops-table__attention' : ''}>
          {user.activeThreads} active
        </strong>
      </td>
      <td data-label="Actionable work">
        <strong className={user.actionable.total ? 'admin-ops-table__attention' : ''}>
          {user.actionable.total} actionable
        </strong>
        {user.actionable.total > 0 && (
          <span>
            {user.actionable.outbox} queued, {user.actionable.blockedOutbox} blocked,
            {' '}{user.actionable.pendingResponses} pending,
            {' '}{user.actionable.deadLetterQueue} dead letter, {user.actionable.processingFailures} failed
          </span>
        )}
      </td>
      <td data-label="Warnings">
        {user.warnings.length ? user.warnings.map(warning => (
          <span className="admin-ops-warning" key={`${warning.code}-${warning.count}`}>
            <AlertTriangle size={14} aria-hidden="true" />
            {warningLabel(warning)}
          </span>
        )) : <span className="admin-ops-table__quiet">None</span>}
      </td>
      <td data-label="Last activity">
        <span className="admin-ops-activity">
          <Clock3 size={14} aria-hidden="true" />
          {formatActivity(user.lastActivityAt)}
        </span>
      </td>
      <td data-label="Actions" className="admin-ops-table__actions">
        <button
          type="button"
          className="admin-ops-review-button"
          onClick={() => onReview(user)}
        >
          <MessagesSquare size={15} aria-hidden="true" />
          Review conversations
        </button>
        {user.supportIssues?.length > 0 && (
          <button
            type="button"
            className="admin-ops-review-button"
            onClick={() => onReviewIssues(user)}
          >
            <Wrench size={15} aria-hidden="true" />
            Review support issues
          </button>
        )}
      </td>
    </tr>
  );
}

function SupportIssuesPanel({ user, onClose }) {
  const label = user.displayName || user.email || user.uid || 'Unknown user';
  return (
    <section className="admin-ops-support" aria-label={`${label} support issues`}>
      <div className="admin-ops-support__heading">
        <div>
          <span>Admin-only diagnostics</span>
          <h3>{label}</h3>
          <p>
            These records are hidden from the user Dashboard. “Outside a live campaign” means the
            related campaign was stopped, archived, or removed before the record was closed.
          </p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close support issues" title="Close">
          <X size={18} aria-hidden="true" />
        </button>
      </div>
      <div className="admin-ops-support__list">
        {user.supportIssues.map(issue => (
          <article key={`${issue.collection}-${issue.id}`}>
            <div>
              <strong>{issue.summary}</strong>
              <span>
                {issue.collection} · {issue.status}
                {issue.outsideLiveCampaign ? ' · outside a live campaign' : ''}
              </span>
            </div>
            <div>
              <span>Campaign {issue.clientId || 'not linked'}</span>
              <span>{formatActivity(issue.createdAt)}</span>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export default function AdminOperationsPage() {
  const [report, setReport] = useState(null);
  const [access, setAccess] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [updateError, setUpdateError] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [savingField, setSavingField] = useState('');
  const [reviewUser, setReviewUser] = useState(null);
  const [supportUser, setSupportUser] = useState(null);
  const [conversationReview, setConversationReview] = useState(EMPTY_CONVERSATION_REVIEW);
  const [reviewClientId, setReviewClientId] = useState(null);
  const [reviewThreadId, setReviewThreadId] = useState(null);
  const [isReviewLoading, setIsReviewLoading] = useState(false);
  const [isReviewThreadLoading, setIsReviewThreadLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviewThreadError, setReviewThreadError] = useState('');
  const reviewRequestVersion = useRef(0);

  const loadOperations = useCallback(async ({ refresh = false } = {}) => {
    refresh ? setIsRefreshing(true) : setIsLoading(true);
    setError('');
    try {
      const [nextReport, nextAccess] = await Promise.all([
        requestAdminOperationsReport(),
        requestCampaignAccessState(),
      ]);
      setReport(nextReport);
      setAccess(nextAccess);
    } catch (loadError) {
      setError(loadError?.message || 'Campaign operations could not be loaded.');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadOperations();
  }, [loadOperations]);

  const requestChange = (field, nextValue) => {
    setUpdateError('');
    setConfirmation({ field, nextValue });
  };

  const confirmChange = async () => {
    if (!confirmation) return;
    const { field, nextValue } = confirmation;
    setSavingField(field);
    setUpdateError('');
    try {
      const nextAccess = await setCampaignAccessState({ [field]: nextValue });
      setAccess(nextAccess);
      setConfirmation(null);
    } catch (accessError) {
      setUpdateError(accessError?.message || 'Campaign access could not be updated.');
      setConfirmation(null);
    } finally {
      setSavingField('');
    }
  };

  const openConversationReview = async user => {
    const requestVersion = reviewRequestVersion.current + 1;
    reviewRequestVersion.current = requestVersion;
    setReviewUser(user);
    setConversationReview(EMPTY_CONVERSATION_REVIEW);
    setReviewClientId(null);
    setReviewThreadId(null);
    setReviewError('');
    setReviewThreadError('');
    setIsReviewLoading(true);
    setIsReviewThreadLoading(false);
    try {
      const nextReview = await requestAdminConversationReview({ targetUid: user.uid });
      if (reviewRequestVersion.current !== requestVersion) return;
      setConversationReview(nextReview);
    } catch (reviewLoadError) {
      if (reviewRequestVersion.current !== requestVersion) return;
      setReviewError(reviewLoadError?.message || 'Conversation review could not be loaded.');
    } finally {
      if (reviewRequestVersion.current === requestVersion) setIsReviewLoading(false);
    }
  };

  const loadReviewThread = async ({ targetUid, clientId, threadId }) => {
    const requestVersion = reviewRequestVersion.current + 1;
    reviewRequestVersion.current = requestVersion;
    setReviewThreadId(threadId);
    setReviewThreadError('');
    setIsReviewThreadLoading(true);
    try {
      const nextReview = await requestAdminConversationReview({
        targetUid,
        clientId,
        threadId,
      });
      if (reviewRequestVersion.current !== requestVersion) return;
      if (!nextReview.threads.some(thread => thread.id === threadId)) {
        throw new Error('Selected conversation was not returned.');
      }
      setConversationReview(current => mergeThreadReview(current, nextReview));
    } catch (reviewLoadError) {
      if (reviewRequestVersion.current !== requestVersion) return;
      setReviewThreadError(reviewLoadError?.message || 'Selected conversation could not be loaded.');
    } finally {
      if (reviewRequestVersion.current === requestVersion) setIsReviewThreadLoading(false);
    }
  };

  const selectReviewCampaign = async clientId => {
    const requestVersion = reviewRequestVersion.current + 1;
    reviewRequestVersion.current = requestVersion;
    setReviewClientId(clientId || null);
    setReviewThreadId(null);
    setReviewError('');
    setReviewThreadError('');
    setIsReviewThreadLoading(false);
    setConversationReview(current => ({
      ...EMPTY_CONVERSATION_REVIEW,
      campaigns: current.campaigns,
      truncation: {
        ...EMPTY_CONVERSATION_REVIEW.truncation,
        campaigns: current.truncation?.campaigns === true,
      },
    }));
    if (!clientId || !reviewUser?.uid) {
      setIsReviewLoading(false);
      return;
    }
    setIsReviewLoading(true);
    try {
      const nextReview = await requestAdminConversationReview({
        targetUid: reviewUser.uid,
        clientId,
      });
      if (reviewRequestVersion.current !== requestVersion) return;
      setConversationReview(nextReview);
      setIsReviewLoading(false);
      const firstThreadId = nextReview.threads[0]?.id;
      if (firstThreadId) {
        void loadReviewThread({
          targetUid: reviewUser.uid,
          clientId,
          threadId: firstThreadId,
        });
      }
    } catch (reviewLoadError) {
      if (reviewRequestVersion.current !== requestVersion) return;
      setReviewError(reviewLoadError?.message || 'Campaign conversations could not be loaded.');
    } finally {
      if (reviewRequestVersion.current === requestVersion) setIsReviewLoading(false);
    }
  };

  const selectReviewThread = threadId => {
    if (!threadId || !reviewUser?.uid || !reviewClientId) return;
    if (threadId === reviewThreadId && isReviewThreadLoading) return;
    const thread = conversationReview.threads.find(candidate => candidate.id === threadId);
    if (thread?.messagesLoaded) {
      reviewRequestVersion.current += 1;
      setReviewThreadId(threadId);
      setReviewThreadError('');
      setIsReviewThreadLoading(false);
      return;
    }
    void loadReviewThread({
      targetUid: reviewUser.uid,
      clientId: reviewClientId,
      threadId,
    });
  };

  const closeConversationReview = () => {
    reviewRequestVersion.current += 1;
    setReviewUser(null);
    setConversationReview(EMPTY_CONVERSATION_REVIEW);
    setReviewClientId(null);
    setReviewThreadId(null);
    setReviewError('');
    setReviewThreadError('');
    setIsReviewLoading(false);
    setIsReviewThreadLoading(false);
  };

  if (isLoading && !report) {
    return (
      <div className="app-layout">
        <SideBar />
        <main className="main-content admin-ops-page">
          <div className="admin-ops-state" role="status">
            <LoaderCircle className="admin-ops-spin" size={24} aria-hidden="true" />
            <strong>Loading campaign operations</strong>
            <span>Reading access controls and privacy-safe activity counts.</span>
          </div>
        </main>
      </div>
    );
  }

  if (error && !report) {
    return (
      <div className="app-layout">
        <SideBar />
        <main className="main-content admin-ops-page">
          <div className="admin-ops-state admin-ops-state--error" role="alert">
            <ShieldAlert size={24} aria-hidden="true" />
            <strong>Campaign operations unavailable</strong>
            <span>{error}</span>
            <button type="button" onClick={() => loadOperations()}>
              <RefreshCw size={16} aria-hidden="true" />
              Retry
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (!report?.summary) {
    return (
      <div className="app-layout">
        <SideBar />
        <main className="main-content admin-ops-page">
          <div className="admin-ops-state admin-ops-state--error" role="alert">
            <ShieldAlert size={24} aria-hidden="true" />
            <strong>Campaign operations unavailable</strong>
            <span>The operations summary is missing from the response.</span>
            <button type="button" onClick={() => loadOperations()}>
              <RefreshCw size={16} aria-hidden="true" />
              Retry
            </button>
          </div>
        </main>
      </div>
    );
  }

  const creationOpen = access?.creationEnabled === true;
  const automationOpen = access?.automationEnabled === true;
  const fullyOpen = creationOpen && automationOpen;
  const accessBanner = accessBannerState(creationOpen, automationOpen);
  const AccessIcon = accessBanner.Icon;
  const summary = report.summary;
  const confirmationCopy = confirmation ? accessCopy(confirmation.field) : null;
  const confirmationAction = confirmation?.nextValue ? 'Enable' : 'Disable';

  return (
    <div className="app-layout">
      <SideBar />
      <main className="main-content admin-ops-page">
        <Header
          stats={[
            { label: 'Live Campaigns', value: summary.campaigns.live },
            { label: 'Active Threads', value: summary.activeThreads },
            { label: 'Actionable Work', value: summary.actionable.total },
            { label: 'Warnings', value: summary.warningCount },
          ]}
        />

        <section className="admin-ops-heading">
          <div>
            <p>Admin operations</p>
            <h2>Campaign operations</h2>
            <span>Aggregate campaign health and release controls without mailbox content or client names.</span>
          </div>
          <button
            type="button"
            className="admin-ops-refresh"
            onClick={() => loadOperations({ refresh: true })}
            disabled={isRefreshing}
            title="Refresh operations"
          >
            <RefreshCw className={isRefreshing ? 'admin-ops-spin' : ''} size={17} aria-hidden="true" />
            {isRefreshing ? 'Refreshing' : 'Refresh'}
          </button>
        </section>

        <PollerHealthPanel />

        <section
          className={`admin-ops-access ${accessBanner.tone}`}
          aria-label="Campaign access controls"
        >
          <div className="admin-ops-access__status">
            <AccessIcon size={22} aria-hidden="true" />
            <div>
              <strong>{accessBanner.headline}</strong>
              <span>{accessBanner.detail}</span>
              {!fullyOpen && access?.maintenanceMessage && (
                <small className="admin-ops-access__notice">
                  Users see: “{access.maintenanceMessage}”
                </small>
              )}
            </div>
          </div>
          <div className="admin-ops-access__controls">
            <AccessToggle
              field="creationEnabled"
              checked={creationOpen}
              disabled={Boolean(savingField)}
              onRequest={requestChange}
            />
            <AccessToggle
              field="automationEnabled"
              checked={automationOpen}
              disabled={Boolean(savingField)}
              onRequest={requestChange}
            />
          </div>
        </section>

        {updateError && <div className="admin-ops-inline-error" role="alert">{updateError}</div>}
        {error && <div className="admin-ops-inline-error" role="alert">{error}</div>}

        <section className="admin-ops-metrics" aria-label="Operations summary">
          <SummaryMetric
            icon={Activity}
            label="Live campaigns"
            value={summary.campaigns.live}
            detail={`${summary.campaigns.stopping} stopping, ${summary.campaigns.stopFailed} stop failed`}
            tone={summary.campaigns.stopFailed ? 'danger' : 'neutral'}
          />
          <SummaryMetric
            icon={Inbox}
            label="Queued work"
            value={`${summary.actionable.outbox} queued`}
            detail={`${summary.actionable.blockedOutbox} blocked before send · ${summary.actionable.pendingResponses} pending responses`}
            tone={summary.actionable.outbox ? 'warning' : 'neutral'}
          />
          <SummaryMetric
            icon={XCircle}
            label="Failures"
            value={`${summary.actionable.processingFailures} processing failures`}
            detail={`${summary.actionable.deadLetterQueue} dead-letter items`}
            tone={summary.actionable.processingFailures || summary.actionable.deadLetterQueue ? 'danger' : 'neutral'}
          />
          <SummaryMetric
            icon={Archive}
            label="Terminal campaigns"
            value={summary.campaigns.completed + summary.campaigns.archived + summary.campaigns.stopped}
            detail={`${summary.campaigns.completed} completed, ${summary.campaigns.archived} archived`}
          />
        </section>

        <section className="admin-ops-breakdown" aria-label="Campaign status totals">
          <div className="admin-ops-breakdown__title">
            <div>
              <span>Campaign status</span>
              <strong>{summary.campaigns.total} campaigns across {summary.userCount} users</strong>
            </div>
            <span className="admin-ops-last-activity">
              <Clock3 size={15} aria-hidden="true" />
              Latest {formatActivity(summary.lastActivityAt)}
            </span>
          </div>
          <div className="admin-ops-status-strip">
            <span><PlayCircle size={15} /> Live <strong>{summary.campaigns.live}</strong></span>
            <span><PauseCircle size={15} /> Stopping <strong>{summary.campaigns.stopping}</strong></span>
            <span><CircleStop size={15} /> Stopped <strong>{summary.campaigns.stopped}</strong></span>
            <span className={summary.campaigns.stopFailed ? 'is-danger' : ''}><XCircle size={15} /> Stop failed <strong>{summary.campaigns.stopFailed}</strong></span>
            <span><CheckCircle2 size={15} /> Completed <strong>{summary.campaigns.completed}</strong></span>
            <span><Archive size={15} /> Archived <strong>{summary.campaigns.archived}</strong></span>
          </div>
        </section>

        <section className="admin-ops-users">
          <div className="admin-ops-users__heading">
            <div>
              <span>Per-user activity</span>
              <h3>Campaign and queue health</h3>
            </div>
            {summary.usersWithWarnings > 0 && (
              <span className="admin-ops-users__warning">
                <AlertTriangle size={15} />
                {summary.usersWithWarnings} {summary.usersWithWarnings === 1 ? 'user needs' : 'users need'} review
              </span>
            )}
          </div>

          {!report.users.length ? (
            <div className="admin-ops-empty">
              <Bot size={23} aria-hidden="true" />
              <strong>No user campaign activity found.</strong>
              <span>The report returned no aggregate user records.</span>
            </div>
          ) : (
            <div className="admin-ops-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col">User</th>
                    {CAMPAIGN_COLUMNS.map(column => <th scope="col" key={column.key}>{column.label}</th>)}
                    <th scope="col">Active threads</th>
                    <th scope="col">Actionable work</th>
                    <th scope="col">Warnings</th>
                    <th scope="col">Last activity</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {report.users.map(user => (
                    <UserOperationsRow
                      user={user}
                      onReview={openConversationReview}
                      onReviewIssues={setSupportUser}
                      key={user.uid}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Per-user SENDING allowance, beside the per-user activity it limits.
            No new route and no new nav item: an operator looking for what a
            user is allowed to do is already on this page. */}
        <AdminSendLimits />

        {supportUser && (
          <SupportIssuesPanel user={supportUser} onClose={() => setSupportUser(null)} />
        )}

        {reviewUser && (
          <AdminConversationReviewDrawer
            user={reviewUser}
            review={conversationReview}
            selectedClientId={reviewClientId}
            selectedThreadId={reviewThreadId}
            loading={isReviewLoading}
            threadLoading={isReviewThreadLoading}
            error={reviewError}
            threadError={reviewThreadError}
            onSelectCampaign={selectReviewCampaign}
            onSelectThread={selectReviewThread}
            onClose={closeConversationReview}
          />
        )}

        <ConfirmDialog
          open={Boolean(confirmation)}
          title={confirmation ? `${confirmationAction} ${confirmationCopy.label.toLowerCase()}?` : ''}
          description={confirmation ? `${confirmationAction} ${confirmationCopy.noun} for normal users. This change takes effect immediately and is recorded in the system audit.` : ''}
          confirmLabel={confirmation ? `${confirmationAction} ${confirmationCopy.noun}` : 'Confirm'}
          variant={confirmation?.nextValue ? 'danger' : 'warning'}
          isLoading={Boolean(savingField)}
          onConfirm={confirmChange}
          onCancel={() => setConfirmation(null)}
        />
      </main>
    </div>
  );
}
